import {
  COGNITO_LAST_AUTH_USER_FIELD,
  COGNITO_STORAGE_KEY_PREFIX,
  LOGIN_CLAIM_PENDING_KEY,
} from "src/domains/shared/utils/storedSessionKeys";
import { SITE_PAGE_KEY_BY_ROUTE } from "./SiteHeader/siteNav";

/**
 * 사이트 틀(`SiteLayout`)을 쓰는 공개 페이지 — `router.pathname` 패턴 그대로. 틀은 이 페이지 키로 머리 정보·지금 페이지
 * 표시를 고르므로 페이지 키 지도에서 만든다(페이지를 더하면 지도 하나만 고친다).
 * `_document` 가 이 페이지에만 사이트 글꼴과 등장 효과 초기값을 넣는다(앱 화면은 받지 않는다).
 */
export const SITE_LAYOUT_ROUTES: ReadonlySet<string> = new Set(
  Object.keys(SITE_PAGE_KEY_BY_ROUTE)
);

/** 로그인·회원가입·비밀번호 변경(marketing-auth-pages)과 오류 화면(없는 주소·준비 중) — 사이트 틀 없이 사이트 글꼴만 쓴다. */
export const SITE_FONT_ONLY_ROUTES: ReadonlySet<string> = new Set([
  "/login",
  "/signup",
  "/changepassword",
  "/404",
  "/prepare",
]);

/** 운영 사이트 주소 — 공유 미리보기·canonical·sitemap 의 절대 주소(dronesquare.co.kr 은 여기로 보낸다) */
export const SITE_ORIGIN = "https://www.dronesquare.co.kr";

/** 공유 미리보기 그림(1200×630, 홈 첫 화면을 찍은 것) */
export const SITE_OG_IMAGE_PATH = "/og/dronesquare.jpg";

export const SITE_FONT_STYLESHEET_HREF = "/fonts/pretendard-v1/pretendard.css";

/**
 * 첫 화면 글자가 든 분할 파일 — 제목이 글꼴 교체로 흔들리지 않게 미리 받는다. 라틴 기본·자주 쓰는 한글(p91·p90)에 더해 홈 첫 화면의
 * 한국어(제목 「드론 기반 인프라 관리 플랫폼」·「문의하기」·「시작하기」·머리말 메뉴·「한국어」)가 쓰는 p89·p88·p87·p85·p83 —
 * 글자가 든 파일은 `siteFontPreload.test` 가 분할 CSS 의 unicode-range 로 확인한다.
 */
export const SITE_FONT_PRELOAD_HREFS = [
  "/fonts/pretendard-v1/p91.woff2",
  "/fonts/pretendard-v1/p90.woff2",
  "/fonts/pretendard-v1/p89.woff2",
  "/fonts/pretendard-v1/p88.woff2",
  "/fonts/pretendard-v1/p87.woff2",
  "/fonts/pretendard-v1/p85.woff2",
  "/fonts/pretendard-v1/p83.woff2",
] as const;

export const SITE_MOTION_ATTR = "data-site-motion";
export const SITE_MOTION_READY_ATTR = "data-site-motion-ready";

/**
 * 첫 칠 전에 등장 효과를 켜는 문서 머리 스크립트. 사이트 HTML 이 그려진 뒤 스크립트가 켜면 보이던 글이 사라졌다가
 * 다시 나타나므로 첫 칠 전에 켠다. 움직임 줄이기·관찰자 미지원이면 켜지 않아 모든 내용이 처음부터 보인다.
 */
export const SITE_MOTION_BOOT_SCRIPT = `try{if(!matchMedia('(prefers-reduced-motion: reduce)').matches&&'IntersectionObserver'in window)document.documentElement.setAttribute('${SITE_MOTION_ATTR}','')}catch(e){}`;

/** 이 브라우저에 확정된 로그인이 저장돼 있으면 `<html>` 에 켜는 속성 — 로그인 확인 전에도 머리말 계정 버튼이 맞는 쪽을 보인다 */
export const SITE_SESSION_ATTR = "data-site-session";

const SESSION_KEY_HEAD = `${COGNITO_STORAGE_KEY_PREFIX}.`;
const SESSION_KEY_TAIL = `.${COGNITO_LAST_AUTH_USER_FIELD}`;

/**
 * 첫 칠 전에 저장된 로그인을 판정하는 문서 머리 스크립트(marketing-site-shell 「머리말 계정 버튼은 첫 화면부터 로그인 상태를
 * 따른다」). 점유 대기 표식이 없고 Amplify 의 `<접두어>.<clientId>.LastAuthUser` 키가 있으면 속성을 켠다 — 로그인 감시자의
 * 「감시할 세션」 판정과 같다. clientId 는 런타임 SSM 값이라 이 시점에는 몰라 접두어·접미어로 찾는다(이 오리진의 앱 클라이언트는
 * 하나다). 저장소를 읽을 수 없으면 켜지 않는다(「로그인」 을 보인다).
 */
export const SITE_SESSION_BOOT_SCRIPT = `try{var s=localStorage;if(s.getItem('${LOGIN_CLAIM_PENDING_KEY}')===null)for(var i=0;i<s.length;i++){var k=s.key(i)||'';if(k.indexOf('${SESSION_KEY_HEAD}')===0&&k.slice(-${SESSION_KEY_TAIL.length})==='${SESSION_KEY_TAIL}'){document.documentElement.setAttribute('${SITE_SESSION_ATTR}','');break}}}catch(e){}`;
